import { Box, Chip, Link } from '@mui/material'
import React, { type ReactNode, useEffect, useState } from 'react'
import { StyledHeader, StyledText } from '../styles'

import GenericButton from '../../../components/GenericButton'
import GenericModal from '../../../components/GenericModal'
import { CreateAndEditTerms } from '../../Terms/CreateAndEditTerms'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import theme from '../../../theme'
import { UploadCsvButton } from '../components/UploadCsvButton'
import { deleteLocallyStoredFieldInSetupTourData, getLocallyStoredDescriptionsByPage, getLocallyStoredTaskIdsByPage, getLocallyStoredTermId, getLocallyStoredTermName, getLocallyStoredUploadStatusesByPage, setLocallyStoredTaskIdsByPage, setLocallyStoredTermIdAndName, setLocallyStoredUploadStatusesAndDescriptionsByPage, updatePendingDescription, useLoadingDots, useTaskStatusQuery } from '../helper'
import { UploadCsvTable } from '../components/UploadCsvTable'
import { WizardSelectionSearch } from '../components/WizardSelectionSearch'
import { courseSectionsTooltipText, instructorAssignmentsTooltipText, studentEnrollmentsTooltipText } from '../components/TooltipText'
import { defaultUploadStatus } from './UploadPeopleAndCoursesPage'
import { defaultCSVUploadDescription, termSetupUploadOptions, wizardKeys } from '../constants'

export const SetupTermPage: React.FC = (): JSX.Element => {
  const options = termSetupUploadOptions
  const loadingDotCount = useLoadingDots()
  const [isCreateTermModalOpen, setIsCreateTermModalOpen] = useState<boolean>(false)
  const [isSelectTermModalOpen, setIsSelectTermModalOpen] = useState<boolean>(false)
  const [termId, setTermId] = useState<string | undefined>(getLocallyStoredTermId())
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, ReactNode>>(getLocallyStoredUploadStatusesByPage(wizardKeys.SetupTermPage, options))
  const [taskIds, setTaskIds] = useState<Record<string, string>>(getLocallyStoredTaskIdsByPage(wizardKeys.SetupTermPage, options))
  const [descriptions, setDescriptions] = useState<Record<string, ReactNode>>(getLocallyStoredDescriptionsByPage(wizardKeys.SetupTermPage, taskIds, loadingDotCount, options))
  const [termName, setTermName] = useState<string | undefined>(getLocallyStoredTermName(wizardKeys.SetupTermPage))

  // locally store termId when updated
  useEffect(() => { setLocallyStoredTermIdAndName(termId, termName) }, [termId])

  // locally store taskIds when updated
  useEffect(() => { setLocallyStoredTaskIdsByPage(wizardKeys.SetupTermPage, taskIds) }, [taskIds])

  // locally store statuses and descriptions when updated
  useEffect(() => { setLocallyStoredUploadStatusesAndDescriptionsByPage(wizardKeys.SetupTermPage, uploadStatuses) }, [uploadStatuses])

  // updates the pending description whenever the status changes
  useEffect(() => { updatePendingDescription(uploadStatuses, setDescriptions, loadingDotCount, options) }, [loadingDotCount, uploadStatuses])

  // fetches status of tasks from BE
  const { data: unusedData } = useTaskStatusQuery(taskIds, uploadStatuses, setUploadStatuses, setDescriptions, loadingDotCount)

  // handling for going back to selecting a term
  // resets all states related to the term setup
  const handleBack = (): void => {
    setTermId(undefined)
    setTermName(undefined)
    setTaskIds(Object.fromEntries(options.map((option: string) => [option, ''])))
    setUploadStatuses(Object.fromEntries(options.map((option: string) => [option, defaultUploadStatus])))
    setDescriptions(Object.fromEntries(options.map((option: string) => [option, defaultCSVUploadDescription])))
    deleteLocallyStoredFieldInSetupTourData(wizardKeys.SetupTermPage)
  }

  // logic for rows in the table
  const isDisabled = React.isValidElement(uploadStatuses.courseSections) && uploadStatuses.courseSections.props?.label !== 'Successful' // only enable if course sections upload is successful
  const disabledText = 'Please upload course sections first'
  const commonProps = { setUploadStatuses, setDescriptions, setTaskIds, loadingDotCount, termId }
  const rows = [
    { id: 1, step: 'Course Sections', upload: <UploadCsvButton value='courseSections' {...commonProps} />, status: uploadStatuses.courseSections, details: descriptions.courseSections, tooltipText: courseSectionsTooltipText },
    { id: 2, step: 'Instructor Assignments', upload: <UploadCsvButton value='instructorAssignments' {...commonProps} />, status: uploadStatuses.instructorAssignments, details: descriptions.instructorAssignments, isDisabled, disabledText, tooltipText: instructorAssignmentsTooltipText },
    { id: 3, step: 'Student Enrollments', upload: <UploadCsvButton value='studentEnrollments' {...commonProps} />, status: uploadStatuses.studentEnrollments, details: descriptions.studentEnrollments, isDisabled, disabledText, tooltipText: studentEnrollmentsTooltipText }
  ]

  return (
    <Box
      flexGrow='1'
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
    >
      {!termId &&
        <>
          <StyledHeader>
            If you already created and set up a term, you can continue to the next step.
          </StyledHeader>
          <StyledText>
            Begin by creating your term
          </StyledText>
          <GenericButton
            label='Create Term'
            variant='contained'
            buttonType={ButtonType.Primary}
            buttonSize={ButtonSize.Large}
            onClick={() => { setIsCreateTermModalOpen(true) }}
          />
          <GenericModal
            isOpen={isCreateTermModalOpen}
            title={'Create Term'}
            onClose={() => { setIsCreateTermModalOpen(false) }}
            maxWidth='sm'
            showCloseIcon={false}
            action={false}
          >
            <CreateAndEditTerms
                handleCloseModal={ () => { setIsCreateTermModalOpen(false) }}
                returnTermId={setTermId}
                returnTermName={setTermName}
            />
          </GenericModal>
          <StyledText>
            Already have a term?
          </StyledText>
          <GenericButton
            label='Select a Term'
            variant='contained'
            buttonType={ButtonType.Secondary}
            buttonSize={ButtonSize.Large}
            onClick={() => { setIsSelectTermModalOpen(true) }}
          />
          <GenericModal
            isOpen={isSelectTermModalOpen}
            title={'Select Term'}
            onClose={() => { setIsSelectTermModalOpen(false) }}
            maxWidth='sm'
            showCloseIcon={false}
            action={false}
          >
            <WizardSelectionSearch
              apiEndpoint='/api/terms/'
              setValue={setTermId}
              setTermName={setTermName}
              setOpenModal={setIsSelectTermModalOpen}
              label='Search for a Term'
              labelField='termName'
              name='termId'
            />
          </GenericModal>
        </>
      }
      {termId &&
        <>
          <Box sx={{ width: '100%', px: theme.spacing(4), pb: theme.spacing(2) }}>
            <StyledHeader textAlign='start' pb={theme.spacing(2)}>
              You are currently setting up the term &quot;{termName}&quot;.
            </StyledHeader>
            <StyledText textAlign='start' fontSize={theme.spacing(3.5)} pb={theme.spacing(2)}>
               <b>Note:</b> Changing the term will save your data, but saves will not be shown below.
            </StyledText>
          </Box>
          <UploadCsvTable rows={rows} />
          <Box sx={{ width: '100%', textAlign: 'flex-start', pl: theme.spacing(10), fontSize: theme.spacing(3.5) }}>
            Wrong term? <Link onClick={handleBack}>Go back</Link>
          </Box>
        </>
      }
    </Box>
  )
}
